import React, { useState } from "react";
import { Button, Tooltip } from "antd";
import { CheckOutlined, RedoOutlined, DeleteOutlined, LoadingOutlined } from "@ant-design/icons";

import AIProcessingButtonFrame from "@/components/ui/AIProcessingButtonFrame";

/**
 * SectionGeneratorDecision renders the action buttons (Keep Changes, Regenerate, Delete)
 * for a single generated section. When `isRegenerating` is true (the section is actively
 * being regenerated), a loading indicator is shown. `isDisabled` also covers sections
 * blocked by another active section, without showing a loading state for the wrong section.
 */
interface SectionGeneratorDecisionProps {
  outputDecision: any;
  section: any;
  handleAccept: any;
  handleRegenerate: any;
  handleDecline: any;
  isRegenerating?: boolean;
  isDisabled?: boolean;
  disabledReason?: string;
}

function SectionGeneratorDecision({
  outputDecision,
  section,
  handleAccept,
  handleRegenerate,
  handleDecline,
  isRegenerating = false,
  isDisabled = false,
  disabledReason,
}: SectionGeneratorDecisionProps) {
  const [isSaving, setIsSaving] = useState(false);

  const handleAcceptChanges = async () => {
    if (isDisabled) return;
    setIsSaving(true);
    try {
      await handleAccept(section);
    } finally {
      setIsSaving(false);
    }
  };

  const isBlockedByOther = Boolean(disabledReason);
  const buttonsDisabled = isDisabled || isSaving || isRegenerating;
  const disabledTooltip = isRegenerating ? "Generation in progress..." : disabledReason;
  const keepTooltip = disabledTooltip || "Keep Section";
  const regenerateTooltip = isRegenerating ? "Generation in progress — click to re-trigger if stuck" : disabledReason || "Regenerate Section";
  const deleteTooltip = disabledReason || "Delete Section";

  return (
    <>
      {outputDecision[section.id] && (
        <div className="section_generator_panel_generated_decision">
          <Tooltip
            placement="bottom"
            title={keepTooltip}
          >
            <Button
              className="default_button"
              onClick={handleAcceptChanges}
              disabled={buttonsDisabled}
            >
              {isSaving ? (
                <>
                  <span className="section_generator_panel_decision_button_text">Saving changes...</span>
                </>
              ) : (
                <>
                  <CheckOutlined style={{ color: buttonsDisabled ? "var(--disabled-text)" : "#fff" }} />
                  <span className="section_generator_panel_decision_button_text"> Keep Changes</span>
                </>
              )}
            </Button>
          </Tooltip>
          <div className="section_generator_panel_generated_decision_button_container">
            <Tooltip
              placement="bottom"
              title={regenerateTooltip}
            >
              <Button
                className="default_button"
                onClick={() => {
                  if (!isBlockedByOther && !isSaving) handleRegenerate(section);
                }}
                disabled={isBlockedByOther || isSaving}
              >
                {isRegenerating ? <LoadingOutlined spin /> : <RedoOutlined />}
              </Button>
            </Tooltip>
            <Tooltip
              placement="bottom"
              title={deleteTooltip}
            >
              <Button
                className="default_button"
                onClick={() => {
                  if (!isBlockedByOther && !isSaving) handleDecline(section);
                }}
                disabled={isBlockedByOther || isSaving}
              >
                <DeleteOutlined />
              </Button>
            </Tooltip>
          </div>
        </div>
      )}
    </>
  );
}

export default SectionGeneratorDecision;
